import { faCommentPen } from "@fortawesome/pro-regular-svg-icons/faCommentPen";
import { Avatar, Grid } from "@mui/material";
import { MprcButton, MprcContainer, MprcIconFa } from "components";
import { MprcFeedTypography } from "components/MprcFeedTypography";
import { MprcSimpleDialog } from "components/MprcSimpleDialog";
import { UserShortView } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import { useSession } from "providers/SessionProvider";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCardContext } from "../CardBuilder";
import { RequestUnblockDialog } from "./RequestUnblockDialog";

////////////////////////////////////////////////////////////////////////////////

type OptionMode = "blocked" | "rejected";

interface ModeratorMessageDialogIProps {
    onClose: () => void;
}

interface BaseModeratorMessageDialogIProps extends ModeratorMessageDialogIProps {
    subtitle?: string;
    moderator: UserShortView;
    comments?: string | null;
    actionText: string;
    actionOn: Date;
    options?: React.ReactNode;
}

////////////////////////////////////////////////////////////////////////////////

const BaseModeratorMessageDialog: React.FC<BaseModeratorMessageDialogIProps> = (props) => {

    const { subtitle, moderator, comments, actionText, actionOn, options, onClose } = props;
    const { t } = useTranslation("knowledge");

    return <MprcSimpleDialog
        title={t("Feed.Card.Actions.ModeratorMessageDialog.Title")}
        subtitle={subtitle}
        onClose={onClose}
        width={"xs"}
        message={
            <>
                <MprcContainer>
                    <Grid container justifyContent="center" alignItems="center" spacing={1}>
                        <Grid>
                            <Avatar src={moderator.thumbnailUrl} title={moderator.name} />
                        </Grid>
                        <Grid size="grow">
                            <MprcFeedTypography variant="authorPrimary" component="div">
                                <span title={moderator.name}>{moderator.name}</span>
                            </MprcFeedTypography>

                            <MprcFeedTypography variant="authorPrimary" component="div">
                                <span>
                                    {actionText}
                                    {" "}
                                    {Formaters.relativeTime(actionOn)}
                                </span>
                            </MprcFeedTypography>
                        </Grid>
                    </Grid>
                </MprcContainer>
                <MprcFeedTypography variant="content" component="div" color={!comments ? "muted" : undefined}>
                    {comments ?? t("Feed.Card.Common.NoModeratorMessageText")}
                </MprcFeedTypography>
            </>
        }
        actions={options}
    />;
};


export const ModeratorMessageBlockDialog: React.FC<ModeratorMessageDialogIProps> = ({ onClose }) => {

    const { pub: { publicationId, title, blocked, author } } = useCardContext();

    const session = useSession();
    const { t } = useTranslation("knowledge");

    const [dialog, setDialog] = useState<any>();

    if (!blocked) return null;

    return (
        <>
            {dialog}
            <BaseModeratorMessageDialog
                actionOn={blocked.blockedOn}
                actionText={t("Feed.Card.Actions.ModeratorMessageDialog.BlockedOnText")}
                moderator={blocked.blocker}
                comments={blocked.message}
                subtitle={title}
                onClose={onClose}
                options={
                    session.user.userId == author.userId &&
                    <MprcButton
                        text={t("Feed.Card.Actions.ModeratorMessageDialog.AskForUnblockButtonText")}
                        variant="contained"
                        color="warning"
                        onClick={() => setDialog(
                            <RequestUnblockDialog
                                publicationId={publicationId}
                                title={title}
                                onClose={(saved) => {

                                    if (saved === true) {
                                        onClose();
                                    }

                                    setDialog(null);
                                }} />
                        )}
                    />
                }

            />
        </>
    );
};

export const ModeratorMessageRejectDialog: React.FC<ModeratorMessageDialogIProps> = ({ onClose }) => {

    const { pub: { title, rejected } } = useCardContext();
    const { t } = useTranslation("knowledge");

    if (!rejected) return null;

    return <BaseModeratorMessageDialog
        actionOn={rejected.rejectedOn}
        actionText={t("Feed.Card.Actions.ModeratorMessageDialog.TypedOnText")}
        moderator={rejected.moderator}
        comments={rejected.message}
        subtitle={title}
        onClose={onClose}
    />;
};

export const ModeratorMessageButton: React.FC<{ optionMode: OptionMode; }> = ({ optionMode }) => {

    const [dialog, setDialog] = useState<any>();
    const { t } = useTranslation("knowledge");

    const openModeratorMessage = () => {
        if (optionMode == "blocked") setDialog(<ModeratorMessageBlockDialog onClose={() => setDialog(null)} />);
        if (optionMode == "rejected") setDialog(<ModeratorMessageRejectDialog onClose={() => setDialog(null)} />);
    };

    return (
        <>
            {dialog}
            {
                <Grid>
                    <MprcButton startIcon={<MprcIconFa icon={faCommentPen} />} color="muted" onClick={openModeratorMessage}>
                        {t("Feed.Card.Common.ModeratorMessageButtonText")}
                    </MprcButton>
                </Grid>
            }
        </>
    );
};
